import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Loader2, Gift, Tag, Copy, ExternalLink, Save, ShieldCheck, Plus, Trash2, KeyRound, X, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const EMPTY_TIER = { name: '', price_cents: 0, description: '', benefits: [], includes_swag_bag: false };

export default function SwagBagTab({ eventId, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [cfg, setCfg] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [aggregate, setAggregate] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwdInput, setPwdInput] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, b] = await Promise.all([
        axios.get(`${API}/sponsorship/event/${eventId}/config`, { headers }),
        axios.get(`${API}/sponsorship/event/${eventId}/all`, { headers }),
        axios.get(`${API}/swag-bag/event/${eventId}/badge`),
      ]);
      const cleaned = {
        custom_tiers: [],
        media_kit: {},
        gate_passwords: [],
        ...c.data,
      };
      setCfg(cleaned);
      setSponsors(s.data.sponsorships || []);
      setAggregate(b.data || {});
    } catch (e) {
      toast.error('Could not load sponsorship data');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line */ }, [eventId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        logo_soup_enabled: cfg.logo_soup_enabled !== false,
        swag_bag_tier_enabled: cfg.swag_bag_tier_enabled !== false,
        swag_bag_enabled: cfg.swag_bag_enabled,
        logo_soup_price_cents: parseInt(cfg.logo_soup_price_cents, 10),
        swag_bag_offer_price_cents: parseInt(cfg.swag_bag_offer_price_cents, 10),
        active_at: cfg.active_at || null,
        gate_message: cfg.gate_message || '',
        custom_tiers: (cfg.custom_tiers || []).map((t, i) => ({
          ...t,
          price_cents: parseInt(t.price_cents, 10) || 0,
          sort_order: i,
        })),
        media_kit: cfg.media_kit || {},
        gate_passwords: cfg.gate_passwords || [],
      };
      const res = await axios.put(`${API}/sponsorship/event/${eventId}/config`, payload, { headers });
      setCfg({ ...res.data, paid_sponsor_count: cfg.paid_sponsor_count });
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setMedia = (k, v) => setCfg({ ...cfg, media_kit: { ...(cfg.media_kit || {}), [k]: v } });

  const addTier = () => setCfg({ ...cfg, custom_tiers: [...(cfg.custom_tiers || []), { ...EMPTY_TIER }] });
  const updateTier = (idx, patch) => {
    const tiers = [...(cfg.custom_tiers || [])];
    tiers[idx] = { ...tiers[idx], ...patch };
    setCfg({ ...cfg, custom_tiers: tiers });
  };
  const removeTier = (idx) => setCfg({ ...cfg, custom_tiers: (cfg.custom_tiers || []).filter((_, i) => i !== idx) });

  const addPwd = () => {
    const v = pwdInput.trim();
    if (!v) return;
    if ((cfg.gate_passwords || []).includes(v)) { toast.error('Already added'); return; }
    setCfg({ ...cfg, gate_passwords: [...(cfg.gate_passwords || []), v] });
    setPwdInput('');
  };
  const removePwd = (p) => setCfg({ ...cfg, gate_passwords: (cfg.gate_passwords || []).filter(x => x !== p) });

  const sponsorUrl = `${window.location.origin}/sponsor/${eventId}`;
  const swagUrl = `${window.location.origin}/swag-bag/${eventId}`;
  const copy = (t, label) => { navigator.clipboard.writeText(t); toast.success(`${label} copied`); };

  if (loading || !cfg) return <div className="py-10 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-4" data-testid="swag-bag-tab">
      {/* Activation */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Sponsorship Activation</h3>
              <p className="text-xs text-muted-foreground">Open this event for sponsor signups. Once enabled, the public sponsor page goes live.</p>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-xs">Enabled</Label>
              <Switch checked={!!cfg.enabled} onCheckedChange={v => setCfg({ ...cfg, enabled: v })} data-testid="swag-enabled-toggle" />
            </div>
          </div>

          {/* Default tiers */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-emerald-500" />Logo Soup tier</p>
                <Switch checked={cfg.logo_soup_enabled !== false} onCheckedChange={v => setCfg({ ...cfg, logo_soup_enabled: v })} data-testid="logo-soup-tier-toggle" />
              </div>
              <Label className="text-[11px]">Price (USD)</Label>
              <Input type="number" min="0" data-testid="swag-logo-soup-price" value={(cfg.logo_soup_price_cents || 0) / 100} onChange={e => setCfg({ ...cfg, logo_soup_price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })} disabled={cfg.logo_soup_enabled === false} />
            </div>
            <div className="border border-border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-indigo-500" />Swag Bag tier</p>
                <Switch checked={cfg.swag_bag_tier_enabled !== false} onCheckedChange={v => setCfg({ ...cfg, swag_bag_tier_enabled: v })} data-testid="swag-tier-toggle" />
              </div>
              <Label className="text-[11px]">Add-on price (USD)</Label>
              <Input type="number" min="0" data-testid="swag-offer-price" value={(cfg.swag_bag_offer_price_cents || 0) / 100} onChange={e => setCfg({ ...cfg, swag_bag_offer_price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })} disabled={cfg.swag_bag_tier_enabled === false} />
              <p className="text-[10px] text-muted-foreground">Charged on top of Logo Soup.</p>
            </div>
          </div>

          {/* Custom tiers */}
          <div className="border border-dashed border-border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-amber-500" />Custom Tiers</p>
              <Button size="sm" variant="outline" onClick={addTier} data-testid="add-custom-tier-btn"><Plus className="h-3 w-3 mr-1" />Add tier</Button>
            </div>
            {(cfg.custom_tiers || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">No custom tiers yet. Add Headline / Bar / VIP / Presenting tiers with their own benefit bullets.</p>
            ) : (cfg.custom_tiers || []).map((t, idx) => (
              <div key={idx} className="border border-border rounded-lg p-3 space-y-2 bg-background" data-testid={`custom-tier-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <Input placeholder="Tier name (e.g. Headline Sponsor)" value={t.name} onChange={e => updateTier(idx, { name: e.target.value })} className="flex-1 font-semibold" data-testid={`tier-name-${idx}`} />
                  <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => removeTier(idx)} data-testid={`remove-tier-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[11px]">Price (USD)</Label>
                    <Input type="number" min="0" value={(t.price_cents || 0) / 100} onChange={e => updateTier(idx, { price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })} data-testid={`tier-price-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-[11px]">Quantity (0 = unlimited)</Label>
                    <Input type="number" min="0" value={t.quantity_total || 0} onChange={e => updateTier(idx, { quantity_total: parseInt(e.target.value || '0', 10) })} data-testid={`tier-quantity-${idx}`} />
                    {typeof t.quantity_sold === 'number' && (
                      <p className="text-[10px] text-muted-foreground mt-1">{t.quantity_sold} sold{t.quantity_total > 0 ? ` of ${t.quantity_total}` : ''}</p>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Switch checked={!!t.includes_swag_bag} onCheckedChange={v => updateTier(idx, { includes_swag_bag: v })} data-testid={`tier-swag-${idx}`} />
                      <Label className="text-xs">Includes Swag Bag offer slot</Label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Description</Label>
                  <Textarea rows={2} value={t.description} onChange={e => updateTier(idx, { description: e.target.value })} placeholder="Short pitch shown on the tier card" />
                </div>
                <div>
                  <Label className="text-[11px]">Benefit bullets (one per line)</Label>
                  <Textarea
                    rows={4}
                    placeholder={"Logo on email blast\nStage shoutout\nLogo on programs"}
                    value={(t.benefits || []).join('\n')}
                    onChange={e => updateTier(idx, { benefits: e.target.value.split('\n').map(s => s).filter(s => s.trim() !== '') })}
                    data-testid={`tier-benefits-${idx}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Gate scheduling */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-3">
              <Switch checked={!!cfg.swag_bag_enabled} onCheckedChange={v => setCfg({ ...cfg, swag_bag_enabled: v })} data-testid="swag-bag-toggle" />
              <Label className="text-xs">Digital Swag Bag enabled (gate page on/off)</Label>
            </div>
            <div>
              <Label className="text-xs">Gate auto-unlock (optional override)</Label>
              <Input type="datetime-local" value={cfg.active_at ? cfg.active_at.slice(0, 16) : ''} onChange={e => setCfg({ ...cfg, active_at: e.target.value || null })} />
              <p className="text-[10px] text-muted-foreground mt-1">Defaults to event start time.</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Gate page message</Label>
              <Textarea rows={2} value={cfg.gate_message || ''} onChange={e => setCfg({ ...cfg, gate_message: e.target.value })} placeholder="Shown above the value breakdown on the locked gate page." />
            </div>
          </div>

          {/* Gate passwords */}
          <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 text-amber-500" />Gate passwords (vault)</p>
            <p className="text-[11px] text-muted-foreground">Print these on Swag Bag cards so guests can unlock the bag at the door. Anyone with a valid password skips the time-gate.</p>
            <div className="flex gap-2">
              <Input value={pwdInput} onChange={e => setPwdInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPwd(); } }} placeholder="e.g. VIP2026" className="text-sm" data-testid="gate-pwd-input" />
              <Button size="sm" onClick={addPwd} data-testid="gate-pwd-add"><Plus className="h-3 w-3 mr-1" />Add</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(cfg.gate_passwords || []).length === 0 ? (
                <p className="text-[11px] text-muted-foreground">No passwords configured.</p>
              ) : (cfg.gate_passwords || []).map(p => (
                <Badge key={p} variant="outline" className="bg-amber-500/10 border-amber-500/30 font-mono pr-1" data-testid={`gate-pwd-chip-${p}`}>
                  {p}
                  <button className="ml-1 hover:text-rose-500" onClick={() => removePwd(p)}><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Media kit */}
          <div className="border border-indigo-500/30 bg-indigo-500/5 rounded-lg p-3 space-y-3" data-testid="media-kit-section">
            <p className="text-xs font-semibold flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-indigo-500" />Media Kit (shown on the public sponsor page)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-[11px]">Expected attendance</Label><Input data-testid="media-attendance" value={cfg.media_kit?.expected_attendance || ''} onChange={e => setMedia('expected_attendance', e.target.value)} placeholder="e.g. 1,200 attendees" /></div>
              <div><Label className="text-[11px]">Estimated reach</Label><Input data-testid="media-reach" value={cfg.media_kit?.estimated_reach || ''} onChange={e => setMedia('estimated_reach', e.target.value)} placeholder="e.g. 45K online + 1.2K onsite" /></div>
              <div><Label className="text-[11px]">Age range</Label><Input value={cfg.media_kit?.age_range || ''} onChange={e => setMedia('age_range', e.target.value)} placeholder="e.g. 25-45" /></div>
              <div><Label className="text-[11px]">Social media reach</Label><Input value={cfg.media_kit?.social_reach || ''} onChange={e => setMedia('social_reach', e.target.value)} placeholder="e.g. 32K IG + 12K newsletter" /></div>
              <div className="sm:col-span-2"><Label className="text-[11px]">Primary demographics</Label><Textarea rows={2} data-testid="media-demographics" value={cfg.media_kit?.demographics || ''} onChange={e => setMedia('demographics', e.target.value)} placeholder="e.g. 60% female, mostly Denver-metro professionals" /></div>
              <div className="sm:col-span-2"><Label className="text-[11px]">Past highlights / extra notes</Label><Textarea rows={2} value={cfg.media_kit?.extra_notes || ''} onChange={e => setMedia('extra_notes', e.target.value)} placeholder="Sold out in 72hrs. Press from Westword." /></div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="swag-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Share links */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold">Public links</h3>
          <div className="flex gap-2 items-center">
            <Input readOnly value={sponsorUrl} className="text-xs font-mono h-8" data-testid="sponsor-public-url" />
            <Button size="sm" variant="outline" onClick={() => copy(sponsorUrl, 'Sponsor link')}><Copy className="h-3 w-3" /></Button>
            <a href={sponsorUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline"><ExternalLink className="h-3 w-3" /></Button></a>
          </div>
          <div className="flex gap-2 items-center">
            <Input readOnly value={swagUrl} className="text-xs font-mono h-8" data-testid="swag-public-url" />
            <Button size="sm" variant="outline" onClick={() => copy(swagUrl, 'Swag bag link')}><Copy className="h-3 w-3" /></Button>
            <a href={swagUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline"><ExternalLink className="h-3 w-3" /></Button></a>
          </div>
        </CardContent>
      </Card>

      {/* Aggregate */}
      <div className="grid sm:grid-cols-4 gap-3">
        <Card className="border-border"><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground">Sponsors</p><p className="text-2xl font-bold" data-testid="metric-sponsor-count">{cfg.paid_sponsor_count || 0}</p></CardContent></Card>
        <Card className="border-border"><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground">Swag offers</p><p className="text-2xl font-bold">{aggregate.offer_count || 0}</p></CardContent></Card>
        <Card className="border-border"><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground">Discount value</p><p className="text-2xl font-bold text-emerald-500">{usd(aggregate.discount_value_cents || 0)}</p></CardContent></Card>
        <Card className="border-border"><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground">Giveaway value</p><p className="text-2xl font-bold text-indigo-500">{usd(aggregate.giveaway_value_cents || 0)}</p></CardContent></Card>
      </div>

      {/* Sponsor list */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold">Sponsorship submissions</h3>
          {sponsors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No sponsors yet — share the public link above.</p>
          ) : sponsors.map(s => (
            <div key={s.sponsorship_id} className="border border-border rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`sponsor-row-${s.sponsorship_id}`}>
              {s.logo_url && <img src={s.logo_url} alt="" className="h-8 w-8 object-contain rounded bg-white/5 p-1" />}
              <div className="flex-1 min-w-[140px]">
                <p className="font-medium text-sm">{s.sponsor_name}</p>
                <p className="text-[11px] text-muted-foreground">{s.sponsor_email}</p>
              </div>
              <Badge variant="outline" className="bg-indigo-500/10 border-indigo-500/30 text-indigo-500">
                {s.tier_name || (s.tier === 'swag_bag' ? 'Swag Bag' : s.tier === 'logo_soup' ? 'Logo Soup' : s.tier)}
              </Badge>
              <Badge variant="outline" className={s.status === 'paid' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}>{s.status}</Badge>
              <span className="text-xs font-mono">{usd(s.amount_paid_cents || s.amount_cents)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
